// TravelEntryForm.js
import React from "react";
import { Box, Typography, TextField, Button, Grid } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ja from "date-fns/locale/ja";
import { saveAs } from "file-saver";
import pako from "pako";

function compressText(text) {
  const utf8Bytes = new TextEncoder().encode(text);
  const compressedBytes = pako.gzip(utf8Bytes);
  const base64String = btoa(String.fromCharCode(...compressedBytes));
  return base64String;
}

function TravelEntryForm({ travelData, setTravelData }) {
  // フィールド変更を即座に親 state に反映するヘルパー
  const updateField = (field, value) => {
    setTravelData((prev) => ({ ...prev, [field]: value }));
  };

  const startDate = travelData.startDate
    ? new Date(travelData.startDate)
    : new Date();
  const endDate = travelData.endDate
    ? new Date(travelData.endDate)
    : (() => {
        let d = new Date();
        d.setDate(d.getDate() + 1);
        return d;
      })();

  const handleStartDateChange = (date) => {
    if (!date) return;
    const dateStr = date.toLocaleDateString();
    setTravelData((prev) => {
      const newData = { ...prev, startDate: dateStr };
      newData.scheduleData = rebuildScheduleData(
        date,
        new Date(prev.endDate),
        prev.scheduleData
      );
      return newData;
    });
  };

  const handleEndDateChange = (date) => {
    if (!date) return;
    const dateStr = date.toLocaleDateString();
    setTravelData((prev) => {
      const newData = { ...prev, endDate: dateStr };
      newData.scheduleData = rebuildScheduleData(
        new Date(prev.startDate),
        date,
        prev.scheduleData
      );
      return newData;
    });
  };

  // 日付範囲変更時に既存スケジュールを保持しつつ日付を再構築
  const rebuildScheduleData = (start, end, existing) => {
    const existingMap = {};
    (existing || []).forEach((day) => {
      existingMap[day.date] = day.schedules;
    });

    const result = [];
    const current = new Date(start);
    while (current <= end) {
      const dateStr = current.toLocaleDateString();
      result.push({
        date: dateStr,
        schedules: existingMap[dateStr] || [],
      });
      current.setDate(current.getDate() + 1);
    }
    return result;
  };

  const handleExport = () => {
    const textData = JSON.stringify(travelData);
    const compressedData = compressText(textData);
    const blob = new Blob([compressedData], {
      type: "text/plain;charset=utf-8",
    });
    saveAs(blob, "travelData.txt");
  };

  const handleGeneratePreviewUrl = async () => {
    try {
      const compressedData = compressText(JSON.stringify(travelData));
      const previewUrl = `${
        window.location.origin
      }/tabi-jaws?travelData=${encodeURIComponent(compressedData)}`;
      await navigator.clipboard.writeText(previewUrl);
      alert("プレビューURLがクリップボードにコピーされました！");
    } catch (error) {
      console.error("URLの生成中にエラーが発生しました:", error);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Box
        sx={{
          maxWidth: 500,
          mx: "auto",
          mt: 4,
          p: 4,
          borderRadius: 3,
          boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.1)",
          background: "linear-gradient(135deg, #FFDEE9 0%, #B5FFFC 100%)",
          color: "#333",
        }}
      >
        <Typography
          variant="h5"
          sx={{ textAlign: "center", fontWeight: "bold", mb: 3 }}
        >
          旅行情報
        </Typography>

        <TextField
          label="タイトル"
          value={travelData.title || ""}
          onChange={(e) => updateField("title", e.target.value)}
          inputProps={{ maxLength: 22 }}
          fullWidth
          sx={{ mb: 2, background: "#fff", borderRadius: 2 }}
        />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <DatePicker
              label="開始日"
              value={startDate}
              onChange={handleStartDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label="終了日"
              value={endDate}
              onChange={handleEndDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
              )}
            />
          </Grid>
        </Grid>

        <TextField
          label="出発地"
          value={travelData.departure || ""}
          onChange={(e) => updateField("departure", e.target.value)}
          inputProps={{ maxLength: 8 }}
          fullWidth
          sx={{ mb: 2, background: "#fff", borderRadius: 2 }}
        />

        <TextField
          label="目的地"
          value={travelData.destination || ""}
          onChange={(e) => updateField("destination", e.target.value)}
          inputProps={{ maxLength: 8 }}
          fullWidth
          sx={{ mb: 2, background: "#fff", borderRadius: 2 }}
        />

        <TextField
          label="メモ"
          value={travelData.notes || ""}
          onChange={(e) => updateField("notes", e.target.value)}
          multiline
          rows={4}
          inputProps={{ maxLength: 1000 }}
          fullWidth
          sx={{ mb: 2, background: "#fff", borderRadius: 2 }}
        />

        {/* Export Button */}
        <Button
          variant="outlined"
          fullWidth
          sx={{
            mt: 2,
            color: "#f46b45",
            ":hover": { backgroundColor: "#f46b45", color: "#fff" },
          }}
          onClick={handleExport}
        >
          データをエクスポート
        </Button>

        {/* Generate Preview URL Button */}
        <Button
          variant="outlined"
          fullWidth
          sx={{
            mt: 2,
            color: "#f46b45",
            ":hover": { backgroundColor: "#f46b45", color: "#fff" },
          }}
          onClick={handleGeneratePreviewUrl}
        >
          プレビューURLを発行
        </Button>
      </Box>
    </LocalizationProvider>
  );
}

export default TravelEntryForm;
